/**
 * Shared types and design tokens for the school management screen and its
 * tabs (ADM-8).
 */
import { brand } from '../../design-system/theme/tokens';

// Professional Design Tokens
export const COLORS = {
  primary: brand.primary,
  secondary: '#F59E0B',
  success: '#10B981',
  error: '#DC2626',
  background: '#FFFFFF',
  surface: '#F9FAFB',
};

export type SchoolManagementTab =
  | 'overview'
  | 'students'
  | 'cohorts'
  | 'engagements'
  | 'mentors'
  | 'documents'
  | 'frameworks'
  | 'settings';

export type StudentFilter = 'all' | 'active' | 'inactive' | 'invited';
export type StudentSort = 'name' | 'activity' | 'transcripts' | 'score';

export interface School {
  id: number;
  name: string;
  slug: string;
  subscription_type: string;
  max_students: number;
  current_students: number;
  subscription_status: string;
  is_active: boolean;
  created_at: string;
}

export interface PendingInvitation {
  id: number;
  email: string;
  full_name: string;
  invited_by?: number | null;
  invitation_expires: string;
  is_accepted: boolean;
  created_at: string;
}

export interface Student {
  id: number;
  email: string;
  full_name: string;
  is_active: boolean;
  created_at: string;
  plan_type?: string | null;
  plan_tier?: string | null;
  subscription_status?: string | null;
  school_subscription_status?: string | null;
  subscription_renews_at?: string | null;
  subscription_source?: string | null;
  package_label?: string | null;
  // Enhanced analytics fields (fetched separately)
  last_activity?: string | null;
  transcripts_this_month?: number;
  transcripts_total?: number;
  monthly_transcripts_limit?: number;
  chat_messages_total?: number;
  avg_icf_score?: number | null;
  // Invitation fields
  is_invited?: boolean;
  invitation_id?: number;
  invitation_expires?: string;
}

export interface RecentAnalysis {
  student_name: string;
  student_email: string;
  overall_score: number;
  analyzed_at: string;
}

export interface InactiveStudent {
  student_id: number;
  full_name: string;
  email: string;
  last_login: string | null;
}

export interface DashboardAnalytics {
  transcripts_this_month: number;
  transcripts_total: number;
  chat_interactions_this_month: number;
  chat_interactions_total: number;
  documents_uploaded: number;
  active_students_week: number;
  total_students: number;
  avg_icf_score: number | null;
  recent_analyses: RecentAnalysis[];
  inactive_students: InactiveStudent[];
}

export interface SchoolDocument {
  id: number;
  filename: string;
  original_filename: string;
  content_type: string;
  coaching_method: string;
  document_type?: string;
  linked_framework_ids?: number[];
  file_size: number;
  processed: boolean;
  chunk_count: number;
  collection_name: string | null;
  created_at: string;
}
